'use client';

import { boardChannel, reached, type Delta, type Reseed, type ReseedScope, type StreamPosition } from '@sharpr/board';
import type { GameCut, Seed } from '@sharpr/projections';
import { skipToken, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PublicationContext, SubscribedContext } from 'centrifuge';
import { useCallback, useEffect, useEffectEvent, useMemo, useRef, useState } from 'react';
import { funnel } from 'remeda';
import { atOrBelow, type AccessTier } from '@/lib/access';
import { applyDeltas, composeBoard, mergeGameCuts, mergeLines, mergeProjections, takesDegradedSeed, unknownPlayers, BOARD_DEGRADED_HEADER, type BoardLines, type BoardProjections } from '@/lib/board/apply';
import { getCentrifugo } from '@/lib/centrifugo/client';
import { fetchError } from '@/lib/fetchError';
import { accessStateKey } from './useAccessState';

// Public viewers refetch only for changes they can see: the release time is visibility (spec §3.7).
const PUBLIC_SCOPES: ReadonlySet<ReseedScope> = new Set(['visibility', 'takeover']);

// A reseed reaches every open board at once, and each board's seed fetch can make a server instance build a seed. So a board
// waits a random part of reseedSpreadMs before it fetches (the builds spread over the window instead of landing together),
// and a seed fetched too early is retried with jittered exponential backoff, not in lockstep.
export interface NbaBoardTiming {
  renderMs: number;        // deltas reach the board (and its readers re-render) at most this often
  reseedSpreadMs: number;  // a reseed's fetch starts at a random point within this window; reseeds within it share one fetch
  retryMs: number;         // a seed older than the reseed that asked for it, or of another epoch (a memo or CDN copy, ≤5s), is fetched again after about this, doubling
  retryMaxMs: number;      // up to this
  pollMs: number;          // the seed refetch while the channel isn't subscribed, or after a failed fetch
}
export const NBA_BOARD_TIMING: NbaBoardTiming = { renderMs: 1_000, reseedSpreadMs: 5_000, retryMs: 1_000, retryMaxMs: 8_000, pollMs: 30_000 };
// Equal jitter (AWS, "Exponential Backoff And Jitter"): half the step fixed, half random, so a board never retries at once.
const backoff = (attempt: number, { retryMs, retryMaxMs }: NbaBoardTiming) => {
  const step = Math.min(retryMaxMs, retryMs * 2 ** attempt);
  return step / 2 + Math.random() * (step / 2);
};

const sleep = (ms: number, signal: AbortSignal) => new Promise<void>((resolve, reject) => {
  const t = setTimeout(resolve, ms);
  signal.addEventListener('abort', () => { clearTimeout(t); reject(signal.reason); }, { once: true });
});
// What the reseeds in one spread window ask for: the changed games at their latest versions, or (any reseed without
// games: a takeover, cuts not stored, a player the board lacks; or naming none: a sport config change on a slate with
// nothing projected, which no cut carries) the whole seed.
type Wanted = { games: Map<string, number>; sportVersion: number } | 'seed';
const want = (w: Wanted | undefined, msg?: Pick<Reseed, 'games' | 'sportVersion'>): Wanted => {
  if (w === 'seed' || !msg?.games || Object.keys(msg.games).length === 0 || msg.sportVersion === undefined) return 'seed';
  const games = new Map(w?.games);
  for (const [id, v] of Object.entries(msg.games)) games.set(id, Math.max(games.get(id) ?? v, v));
  return { games, sportVersion: Math.max(w?.sportVersion ?? 0, msg.sportVersion) };
};
const LINES_KEY = ['board', 'nba', 'lines'] as const;
const PROJECTIONS_KEY = ['board', 'nba', 'projections'] as const;
// The later of two positions; across epochs (a Centrifugo restart) the new ask's, as the old epoch's offsets are gone.
const later = (a: StreamPosition | undefined, b: StreamPosition | undefined) => (a && b && a.epoch === b.epoch && reached(a, b) ? a : b ?? a);

// The NBA seed kept live (spec §4.4): the viewer's seed, resumed on the board:nba stream from the seed's own position. The
// odds screen and the analyst workspace each read their own view of the board it returns.
// `tier`: the viewer's NBA access tier (useAccess: the claim until the live status loads); the server cuts the seed by
// its own live check, and readers show the view it sent. `onReload`: after a seed lands on held projections (a reseed,
// a poll, or a gap the stream couldn't cover).
// The board lives in the query cache in two halves keyed by what each depends on: the prop lines (['board', 'nba',
// 'lines'], one copy for every view, kept current by the stream's deltas, written at most once per renderMs) and the
// projections of the viewer's tier (['board', 'nba', 'projections', tier]). A seed fetch pushes its lines into the lines
// entry and returns its projections; both merge under one rule, newer wins (lib/board/apply). So the board outlives its
// readers (a sport switch and back resumes it) and an access change swaps projections over the same live lines.
export function useNbaLiveSeed({ enabled, tier, onReload }: {
  enabled: boolean; tier: AccessTier; onReload?: () => void;
}, timing: NbaBoardTiming = NBA_BOARD_TIMING) {
  const queryClient = useQueryClient();
  const url = tier === 'public' ? '/api/board/nba/public' : `/api/board/nba?view=${tier}`;
  const queryKey = useMemo(() => [...PROJECTIONS_KEY, tier] as const, [tier]);
  // The key in use, read by the stream's callbacks.
  const currentKey = useEffectEvent(() => queryKey);
  // The latest position a reseed or an unknown player asked a seed to reach, for every view's fetch (positions only grow,
  // so it is never cleared); players a delta named that the board lacked; the stream's epoch while subscribed; deltas
  // waiting for the next write; whether the stream has asked for state yet.
  const target = useRef<StreamPosition | undefined>(undefined);
  const awaiting = useRef(new Map<string, StreamPosition>());
  const epoch = useRef<string | undefined>(undefined);
  const pending = useRef<Array<{ delta: Delta; at?: StreamPosition }>>([]);
  const asked = useRef(false);
  const onReloadRef = useRef(onReload);
  useEffect(() => { onReloadRef.current = onReload; }, [onReload]);
  const [subscribed, setSubscribed] = useState(false);

  // The lines are observed, never fetched here: the projections query's fetch pushes them.
  const linesQuery = useQuery<BoardLines>({ queryKey: LINES_KEY, queryFn: skipToken, staleTime: Infinity, structuralSharing: false });
  const query = useQuery<BoardProjections>({
    queryKey,
    enabled,
    staleTime: Infinity,
    structuralSharing: false,
    // A switch of view keeps the old projections over the live lines until its own land, unless they are above it
    // (access lost): nothing shows then.
    placeholderData: prev => (prev && atOrBelow(prev.seed.view, tier) ? prev : undefined),
    // The stream keeps the board current; without it, or after a failed fetch, the seed is polled; a degraded seed (the
    // public view while the access check was unavailable) is asked for again sooner.
    refetchInterval: q => (q.state.data?.degraded ? timing.retryMaxMs : !subscribed || q.state.status === 'error' ? timing.pollMs : false),
    // Fetches seeds until one is read at or after the latest position asked for, and in the stream's epoch while
    // subscribed (a memo or CDN copy can be older, or from before a Centrifugo restart, which positions can't order),
    // then merges its lines into the shared lines and its projections into the view's, each as it is at that moment.
    queryFn: async ({ signal }): Promise<BoardProjections> => {
      let seed: Seed;
      let degraded = false;
      for (let attempt = 0; ; attempt++) {
        const res = await fetch(url, { signal });
        if (!res.ok) throw await fetchError(res);
        degraded = res.headers.get(BOARD_DEGRADED_HEADER) === '1';
        if (degraded && !takesDegradedSeed(queryClient.getQueryData<BoardProjections>(queryKey))) {
          throw new Error('Seed without its paid data: the board keeps the projections it holds');
        }
        seed = await res.json() as Seed;
        const otherEpoch = epoch.current !== undefined && seed.position !== undefined && seed.position.epoch !== epoch.current;
        if (!otherEpoch && (!target.current || reached(seed.position, target.current))) break;
        await sleep(backoff(attempt, timing), signal);
      }
      queryClient.setQueryData<BoardLines>(LINES_KEY, held => mergeLines(held, seed));
      const held = queryClient.getQueryData<BoardProjections>(queryKey);
      const merged = mergeProjections(held, seed, awaiting.current);
      const next = degraded ? { ...merged, degraded: true as const } : merged;
      for (const k of [...awaiting.current.keys()]) if (next.players.has(k) || next.absent.has(k)) awaiting.current.delete(k);
      if (held) onReloadRef.current?.();
      return next;
    },
  });

  // A newer seed is needed: the fetch in flight, or a new one, keeps going until it reaches `at`. A reseed cancels
  // nothing, so a stream of asks can't keep a seed from landing (a manual refetch does: the fetch in flight may predate
  // it). Every view's projections go stale, so one switched back to refetches too.
  const reloadSeed = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: PROJECTIONS_KEY }, { cancelRefetch: false });
  }, [queryClient]);

  // A reseed naming its games: one request for them, merged over the view's projections (lib/board/apply). Cuts not
  // stored yet (503) are asked for again with the seed's backoff; past its cap, or on any other failure, the seed is
  // refetched. Games the response leaves out keep what the board holds. The other views go stale.
  const loading = useRef<AbortController>(undefined);
  useEffect(() => {
    const c = new AbortController();
    loading.current = c;
    return () => c.abort();
  }, []);
  const loadGames = useCallback(async (key: typeof queryKey, { games, sportVersion }: Exclude<Wanted, 'seed'>) => {
    void queryClient.invalidateQueries({ queryKey: PROJECTIONS_KEY, predicate: q => q.queryKey[3] !== key[3] });
    const ids = [...games.keys()];
    const signal = loading.current?.signal;
    if (!signal) return;
    const url = `/api/board/nba/games?ids=${ids.join(',')}&v=${ids.map(id => games.get(id)).join(',')}&sv=${sportVersion}&view=${key[3]}`;
    try {
      for (let attempt = 0; ; attempt++) {
        const res = await fetch(url, { signal });
        if (res.ok) {
          const { games: cuts } = await res.json() as { games: GameCut[] };
          const held = queryClient.getQueryData<BoardProjections>(key);
          if (!held) return reloadSeed();
          const next = mergeGameCuts(held, cuts);
          if (next !== held) {
            queryClient.setQueryData<BoardProjections>(key, next);
            onReloadRef.current?.();
          }
          return;
        }
        if (res.status !== 503 || timing.retryMs * 2 ** attempt >= timing.retryMaxMs) throw await fetchError(res);
        await sleep(backoff(attempt, timing), signal);
      }
    } catch {
      if (!signal.aborted) reloadSeed();
    }
  }, [queryClient, reloadSeed, timing]);

  // The fetch waits a random part of reseedSpreadMs (every open board gets the reseed at once); reseeds meanwhile only move
  // the target, which that fetch reads, and add what they ask for. The public view reads no cuts: it refetches its seed.
  const spread = useRef<ReturnType<typeof setTimeout>>(undefined);
  const keyNow = useRef(queryKey);
  useEffect(() => { keyNow.current = queryKey; }, [queryKey]);
  const wanted = useRef<Wanted | undefined>(undefined);
  const reseed = useCallback((at?: StreamPosition, msg?: Pick<Reseed, 'games' | 'sportVersion'>) => {
    target.current = later(target.current, at);
    wanted.current = want(wanted.current, msg);
    if (spread.current !== undefined) return;
    spread.current = setTimeout(() => {
      spread.current = undefined;
      const w = wanted.current;
      wanted.current = undefined;
      const key = keyNow.current;
      if (w === 'seed' || !w || key[3] === 'public' || !queryClient.getQueryData(key)) reloadSeed();
      else void loadGames(key, w);
    }, Math.random() * timing.reseedSpreadMs);
  }, [queryClient, reloadSeed, loadGames, timing.reseedSpreadMs]);
  useEffect(() => () => { clearTimeout(spread.current); spread.current = undefined; }, []);

  // Pending deltas into the cached board, at most once per renderMs. A player the board lacks asks for a seed once.
  const writer = useRef<{ call: () => void }>(undefined);
  useEffect(() => {
    const f = funnel(() => {
      const held = queryClient.getQueryData<BoardLines>(LINES_KEY);
      const batch = pending.current;
      pending.current = [];
      if (!held || batch.length === 0) return;
      const deltas = batch.map(b => b.delta);
      queryClient.setQueryData<BoardLines>(LINES_KEY, applyDeltas(held, deltas));
      const projections = queryClient.getQueryData<BoardProjections>(currentKey());
      if (!projections) return;
      const at = batch.reduce<StreamPosition | undefined>((a, b) => later(a, b.at), undefined);
      let asks = false;
      for (const k of unknownPlayers(projections, deltas)) if (at && !awaiting.current.has(k)) { awaiting.current.set(k, at); asks = true; }
      if (asks) reseed(at);
    }, { minGapMs: timing.renderMs, triggerAt: 'both' });
    writer.current = f;
    return () => f.cancel();
  }, [queryClient, reseed, timing.renderMs]);

  const onPublication = useEffectEvent((ctx: PublicationContext) => {
    const msg = ctx.data as Delta | Reseed;
    if (msg.sport !== 'nba') return;
    const at = ctx.offset !== undefined && epoch.current !== undefined ? { offset: ctx.offset, epoch: epoch.current } : undefined;
    if (msg.type === 'reseed') {
      // A change the public view can't see: its projections stay, every other view's go stale (and wait for it).
      if (currentKey()[3] === 'public' && !msg.scopes.some(s => PUBLIC_SCOPES.has(s))) {
        target.current = later(target.current, at);
        void queryClient.invalidateQueries({ queryKey: PROJECTIONS_KEY, predicate: q => q.queryKey[3] !== 'public' });
        return;
      }
      reseed(at, msg);
      return;
    }
    if (msg.type !== 'props') return;
    pending.current.push({ delta: msg, at });
    writer.current?.call();
  });

  // The stream from the seed's position: the SDK asks for state on the first subscribe and whenever history can't cover
  // a gap (spec §13.4), and replays everything published after it, so nothing is missed between seed and stream. The
  // board already held (this page's first load, or one cached from an earlier visit) serves the first ask; any later ask
  // (a gap) fetches, or joins, a new seed.
  useEffect(() => {
    if (!enabled) return;
    const socket = getCentrifugo();
    if (!socket) return;
    const channel = boardChannel('nba');
    const sub = socket.getSubscription(channel) ?? socket.newSubscription(channel, {
      getState: async () => {
        const queryKey = currentKey();
        const first = !asked.current && queryClient.getQueryData(LINES_KEY) !== undefined;
        asked.current = true;
        // A gap may have lost reseeds: every view's projections go stale, and this one reloads.
        if (!first) await queryClient.invalidateQueries({ queryKey: PROJECTIONS_KEY }, { cancelRefetch: false, throwOnError: true });
        else if (queryClient.isFetching({ queryKey, exact: true }) > 0) {
          await queryClient.refetchQueries({ queryKey, exact: true }, { cancelRefetch: false, throwOnError: true });
        }
        const position = queryClient.getQueryData<BoardLines>(LINES_KEY)?.position;
        if (!position) throw new Error('the seed carries no stream position');
        return position;
      },
    });
    const up = (ctx: SubscribedContext) => { epoch.current = ctx.streamPosition?.epoch; setSubscribed(true); };
    // Unsubscribed, the epoch is unknown: Centrifugo may restart into a new one before the next subscribe.
    const down = () => { epoch.current = undefined; setSubscribed(false); };
    sub.on('subscribed', up);
    sub.on('subscribing', down);
    sub.on('unsubscribed', down);
    sub.on('publication', onPublication);
    sub.subscribe();
    return () => {
      sub.off('subscribed', up);
      sub.off('subscribing', down);
      sub.off('unsubscribed', down);
      sub.off('publication', onPublication);
      sub.unsubscribe();
      socket.removeSubscription(sub);
      setSubscribed(false);
    };
  }, [enabled, queryClient]);

  const projections = query.data;
  // The server sent another view than the client's tier (a stale claim or status): the access status reloads, and its
  // tier corrects the key. Readers show the view the server sent either way.
  const served = query.isPlaceholderData || projections?.degraded ? undefined : projections?.seed.view;
  useEffect(() => {
    if (served && served !== tier) void queryClient.invalidateQueries({ queryKey: accessStateKey });
  }, [served, tier, queryClient]);
  const lines = linesQuery.data;
  const board = useMemo(() => (projections && lines ? composeBoard(lines, projections) : undefined), [projections, lines]);
  // A board already shown stays shown through a failed fetch (the query retries, then polls); only a first load that
  // fails shows its error.
  const error = board ? undefined : query.error ?? undefined;
  const refetchSeed = query.refetch;
  const refetch = useCallback(() => refetchSeed().then(() => {}), [refetchSeed]);
  return { board, error, refetch, subscribed };
}
